import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import type { SelectOption } from '../../../../React/Common/Components/Selects/Select/Select'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, MessageContext, PromptContext } from '../../../../React/Common/Contexts'
import type { MessageContextType } from '../../../../React/Common/Contexts/MessageContext'
import type { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import { Router as RouterLang, MessagesLanguage } from '../../../../../i18n'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { OutlinedSelect } from '../../Atoms/Inputs/OutlinedSelect'
import { OutlinedTextInput } from '../../Atoms/Inputs/OutlinedTextInput'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import {
    deleteMessageDefinition,
    getMessageDefinition,
    getMessageLanguages,
    getMessageTypes,
    putMessageDefinition
} from '../../../../React/Common/API/Messages/MessagesAPI'
import type {
    EditableMessage,
    MessageDefinitionPutRequest,
    MessageDefinitionResponse,
    MessageLanguagesResponse,
    MessageTypesResponse
} from '../../../../NonReact/Types/MessagesTypes'
import { DEFAULT_LOCALE_SELECT_VALUE } from '../../../Constants/MessagesConstants'

const MESSAGES_PAGE_SELECTOR_CLASS = 'messages-page-selector'
const MESSAGES_PAGE_OUTLINED_SELECT_CLASS = 'messages-page-outlined-select'
const MESSAGES_PAGE_FIELD_CLASS = 'messages-page-field'
const MESSAGES_PAGE_ACTION_BUTTON_CLASS = 'messages-page-action-button'

const EMPTY_MESSAGE: EditableMessage = {
    subject: EMPTY_STRING,
    plainBody: EMPTY_STRING,
    htmlBody: EMPTY_STRING
}

const mapMessageResponse = (response: Nullable<MessageDefinitionResponse>): EditableMessage => ({
    subject: response?.Data?.subject ?? EMPTY_STRING,
    plainBody: response?.Data?.plainBody ?? EMPTY_STRING,
    htmlBody: response?.Data?.htmlBody ?? EMPTY_STRING
})

/**
 * Routed Messages editor page.
 */
export const Messages: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundles([RouterLang.NAME, MessagesLanguage.NAME])

    const { displayError, displayErrorMessage, displaySuccessMessage }: MessageContextType = useContext(MessageContext)
    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const [typeOptions, setTypeOptions] = useState<readonly SelectOption<string>[]>([])
    const [languageOptions, setLanguageOptions] = useState<readonly SelectOption<string>[]>([])
    const [selectedTypeId, setSelectedTypeId] = useState<Nullable<string>>(null)
    const [selectedLocale, setSelectedLocale] = useState<string>(DEFAULT_LOCALE_SELECT_VALUE)
    const [messageDef, setMessageDef] = useState<Nullable<EditableMessage>>(null)
    const [isDirty, setIsDirty] = useState<boolean>(false)
    const [isNew, setIsNew] = useState<boolean>(false)
    const [canEdit, setCanEdit] = useState<boolean>(false)
    const [canDelete, setCanDelete] = useState<boolean>(false)
    const [loadingCount, setLoadingCount] = useState<number>(0)
    const [isSaving, setIsSaving] = useState<boolean>(false)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const isLoading: boolean = loadingCount > 0

    const localeSelectOptions: readonly SelectOption<string>[] = useMemo(
        (): readonly SelectOption<string>[] => [
            {
                label: language.get(MessagesLanguage.Messages_Language_DefaultOption),
                value: DEFAULT_LOCALE_SELECT_VALUE
            },
            ...languageOptions
        ],
        [language, languageOptions]
    )

    const selectedLocaleValue: string = selectedLocale === DEFAULT_LOCALE_SELECT_VALUE ? EMPTY_STRING : selectedLocale

    const loadMessage = useCallback(
        (typeId: string, locale: string): Promise<void> => {
            setLoadingCount((current: number): number => current + 1)

            return getMessageDefinition(typeId, locale)
                .then((response: Nullable<MessageDefinitionResponse>): void => {
                    if (!isMounted.current) return

                    setMessageDef(mapMessageResponse(response))
                    setIsNew(Boolean(response?.Data?.isNew))
                    setCanEdit(Boolean(response?.Data?.canEdit))
                    setCanDelete(Boolean(response?.Data?.canDelete))
                    setIsDirty(false)
                })
                .catch((error: Error): void => {
                    displayError(error)
                })
                .finally((): void => {
                    if (!isMounted.current) return

                    setLoadingCount((current: number): number => current - 1)
                })
        },
        [displayError, isMounted]
    )

    useEffect((): void => {
        setLoadingCount((current: number): number => current + 1)

        Promise.all([getMessageTypes(), getMessageLanguages()])
            .then(
                ([typesResponse, languagesResponse]: [
                    Nullable<MessageTypesResponse>,
                    Nullable<MessageLanguagesResponse>
                ]): void => {
                    if (!isMounted.current) return

                    const nextTypeOptions: readonly SelectOption<string>[] =
                        typesResponse?.Data?.items?.map(
                            (item): SelectOption<string> => ({
                                label: item.description,
                                value: item.typeId
                            })
                        ) ?? []

                    const nextLanguageOptions: readonly SelectOption<string>[] =
                        languagesResponse?.Data?.items?.map(
                            (item): SelectOption<string> => ({
                                label: item.description,
                                value: item.locale
                            })
                        ) ?? []

                    setTypeOptions(nextTypeOptions)
                    setLanguageOptions(nextLanguageOptions)
                    setSelectedTypeId(
                        (current: Nullable<string>): Nullable<string> => current ?? nextTypeOptions[0]?.value ?? null
                    )
                }
            )
            .catch((error: Error): void => {
                displayError(error)
            })
            .finally((): void => {
                if (!isMounted.current) return

                setLoadingCount((current: number): number => current - 1)
            })
    }, [displayError, isMounted])

    useEffect((): void => {
        if (IsVarEmpty(selectedTypeId)) return

        loadMessage(selectedTypeId, selectedLocaleValue)
    }, [loadMessage, selectedLocaleValue, selectedTypeId])

    const confirmDiscardChanges = useCallback(async (): Promise<boolean> => {
        if (!isDirty) return true

        try {
            await displayPromptPromise(
                language.get(MessagesLanguage.Messages_DiscardChanges_Prompt),
                language.get(MessagesLanguage.Messages_DiscardChanges_Confirm),
                language.get(MessagesLanguage.Messages_DiscardChanges_Cancel)
            )

            return true
        } catch {
            // User cancelled the discard prompt.
            return false
        }
    }, [displayPromptPromise, isDirty, language])

    const updateMessageField = useCallback((field: keyof EditableMessage, value: string): void => {
        setMessageDef((current: Nullable<EditableMessage>): Nullable<EditableMessage> => {
            if (IsVarEmpty(current)) return current

            return {
                ...current,
                [field]: value
            }
        })
        setIsDirty(true)
    }, [])

    const handleTypeChange = useCallback(
        (typeId: string): void => {
            if (typeId === selectedTypeId) return

            confirmDiscardChanges().then((shouldDiscardChanges: boolean): void => {
                if (!shouldDiscardChanges) return

                setSelectedLocale(DEFAULT_LOCALE_SELECT_VALUE)
                setSelectedTypeId(typeId)
            })
        },
        [confirmDiscardChanges, selectedTypeId]
    )

    const handleLocaleChange = useCallback(
        (locale: string): void => {
            if (locale === selectedLocale) return

            confirmDiscardChanges().then((shouldDiscardChanges: boolean): void => {
                if (!shouldDiscardChanges) return

                setSelectedLocale(locale)
            })
        },
        [confirmDiscardChanges, selectedLocale]
    )

    const handleCancel = useCallback((): void => {
        if (IsVarEmpty(selectedTypeId)) return

        confirmDiscardChanges().then((shouldDiscardChanges: boolean): void => {
            if (!shouldDiscardChanges) return

            if (isNew && isVarNotEmpty(selectedLocaleValue)) {
                setSelectedLocale(DEFAULT_LOCALE_SELECT_VALUE)
                return
            }

            loadMessage(selectedTypeId, selectedLocaleValue)
        })
    }, [confirmDiscardChanges, isNew, loadMessage, selectedLocaleValue, selectedTypeId])

    const handleSave = useCallback((): void => {
        if (!canEdit || IsVarEmpty(selectedTypeId) || IsVarEmpty(messageDef)) return

        const payload: MessageDefinitionPutRequest = {
            subject: messageDef.subject,
            plainBody: messageDef.plainBody,
            htmlBody: messageDef.htmlBody
        }

        setIsSaving(true)

        putMessageDefinition(selectedTypeId, selectedLocaleValue, payload)
            .then((): Promise<void> => loadMessage(selectedTypeId, selectedLocaleValue))
            .then((): void => {
                if (!isMounted.current) return

                displaySuccessMessage(language.get(MessagesLanguage.Messages_Save_Success))
            })
            .catch((): void => {
                displayErrorMessage(language.get(MessagesLanguage.Messages_Save_Error))
            })
            .finally((): void => {
                if (!isMounted.current) return

                setIsSaving(false)
            })
    }, [
        canEdit,
        displayErrorMessage,
        displaySuccessMessage,
        isMounted,
        language,
        loadMessage,
        messageDef,
        selectedLocaleValue,
        selectedTypeId
    ])

    const handleDelete = useCallback((): void => {
        if (!canDelete || IsVarEmpty(selectedTypeId) || selectedLocaleValue === EMPTY_STRING) return

        setIsSaving(true)

        deleteMessageDefinition(selectedTypeId, selectedLocaleValue)
            .then((): void => {
                if (!isMounted.current) return

                displaySuccessMessage(language.get(MessagesLanguage.Messages_Delete_Success))
                setSelectedLocale(DEFAULT_LOCALE_SELECT_VALUE)
            })
            .catch((): void => {
                displayErrorMessage(language.get(MessagesLanguage.Messages_Delete_Error))
            })
            .finally((): void => {
                if (!isMounted.current) return

                setIsSaving(false)
            })
    }, [canDelete, displayErrorMessage, displaySuccessMessage, isMounted, language, selectedLocaleValue, selectedTypeId])

    const shouldShowEditor: boolean = isVarNotEmpty(messageDef)
    const shouldShowSaveActions: boolean = shouldShowEditor && (isDirty || isNew)
    const shouldShowDelete: boolean = shouldShowEditor && !isDirty && !isNew && canDelete
    const areSelectorsDisabled: boolean = isLoading || isSaving || typeOptions.length === 0
    const areFieldsDisabled: boolean = !canEdit || isLoading || isSaving || IsVarEmpty(messageDef)
    const isDeleteDisabled: boolean = isLoading || isSaving || selectedLocaleValue === EMPTY_STRING

    const currentMessage: EditableMessage = messageDef ?? EMPTY_MESSAGE

    return (
        <div className='messages-page admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_messages} />
            <div className='messages-page-selectors'>
                <div className={MESSAGES_PAGE_SELECTOR_CLASS} data-testid='messages-type-select'>
                    <OutlinedSelect<string>
                        label={language.get(MessagesLanguage.Messages_Type_Label)}
                        options={[...typeOptions]}
                        value={selectedTypeId ?? EMPTY_STRING}
                        disabled={areSelectorsDisabled}
                        handleChange={handleTypeChange}
                        containerClassName={MESSAGES_PAGE_OUTLINED_SELECT_CLASS}
                    />
                </div>
                <div className={MESSAGES_PAGE_SELECTOR_CLASS} data-testid='messages-locale-select'>
                    <OutlinedSelect<string>
                        label={language.get(MessagesLanguage.Messages_Language_Label)}
                        options={[...localeSelectOptions]}
                        value={selectedLocale}
                        disabled={IsVarEmpty(selectedTypeId) || isLoading || isSaving}
                        handleChange={handleLocaleChange}
                        containerClassName={MESSAGES_PAGE_OUTLINED_SELECT_CLASS}
                    />
                </div>
            </div>

            {shouldShowEditor ? (
                <div className='messages-page-editor'>
                    {isNew ? (
                        <div className='messages-page-new-locale-banner' data-testid='messages-new-locale-banner'>
                            {language.get(MessagesLanguage.Messages_NewLocale_Info)}
                        </div>
                    ) : null}

                    <div className={MESSAGES_PAGE_FIELD_CLASS} data-testid='messages-subject-input'>
                        <OutlinedTextInput
                            labelLeft={language.get(MessagesLanguage.Messages_Subject_Label)}
                            value={currentMessage.subject}
                            disabled={areFieldsDisabled}
                            onChange={(value: string): void => updateMessageField('subject', value)}
                            containerClassName='messages-page-text-input'
                        />
                    </div>

                    <div className={MESSAGES_PAGE_FIELD_CLASS} data-testid='messages-plain-body-input'>
                        <OutlinedTextInput
                            labelLeft={language.get(MessagesLanguage.Messages_PlainBody_Label)}
                            value={currentMessage.plainBody}
                            disabled={areFieldsDisabled}
                            multiline
                            rows={10}
                            onChange={(value: string): void => updateMessageField('plainBody', value)}
                            containerClassName='messages-page-text-input messages-page-textarea'
                        />
                    </div>

                    <div className={MESSAGES_PAGE_FIELD_CLASS} data-testid='messages-html-body-input'>
                        <OutlinedTextInput
                            labelLeft={language.get(MessagesLanguage.Messages_HtmlBody_Label)}
                            value={currentMessage.htmlBody}
                            disabled={areFieldsDisabled}
                            multiline
                            rows={12}
                            onChange={(value: string): void => updateMessageField('htmlBody', value)}
                            containerClassName='messages-page-text-input messages-page-textarea'
                        />
                    </div>

                    <div className='messages-page-actions'>
                        {shouldShowSaveActions ? (
                            <>
                                <IconButton
                                    customClassName={MESSAGES_PAGE_ACTION_BUTTON_CLASS}
                                    iconName={IconName.CHECK}
                                    type={IconButtonType.BUTTON}
                                    title={language.get(MessagesLanguage.Messages_Action_Save)}
                                    onClick={handleSave}
                                    isEnabled={canEdit && !isLoading && !isSaving}
                                />
                                <IconButton
                                    customClassName={MESSAGES_PAGE_ACTION_BUTTON_CLASS}
                                    iconName={IconName.RESTORE_FROM_TRASH}
                                    type={IconButtonType.BUTTON}
                                    title={language.get(MessagesLanguage.Messages_Action_Revert)}
                                    onClick={handleCancel}
                                    isEnabled={!isLoading && !isSaving}
                                />
                            </>
                        ) : null}
                        {shouldShowDelete ? (
                            <IconButton
                                customClassName={`${MESSAGES_PAGE_ACTION_BUTTON_CLASS} messages-page-delete-button`}
                                iconName={IconName.DELETE}
                                type={IconButtonType.BUTTON}
                                title={language.get(MessagesLanguage.Messages_Action_Delete)}
                                onClick={handleDelete}
                                isEnabled={!isDeleteDisabled}
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    )
}
